<script lang="ts">
  import { get, writable } from "svelte/store";
  import { onMount } from "svelte";
  import SearchHeader from "../../components/SearchHeader.svelte";
  import type {
    ICompanyInvoiceDTO,
    ICompanyInvoiceEmailDTO,
  } from "../../services/DTO";
  import { companyInvoicePaymentDtoFields } from "../../utils/TableFields/company-invoice-payment-dto.fields";
  import { PermissionService } from "../../utils/misc/permission-utils";

  import { user } from "../../app.store";
  import {
    loading,
    loadWithUpdate,
    record,
    records,
    filter,
    search,
    save,
    initList,
    resetSearchFilter,
    sendEmails,
    onExport,
  } from "./unpaidinvoices.store";
  import SelectableTable from "../../components/Table/SelectableTable.svelte";
  import type { ICompanyInvoicePayment } from "../../entities/company-invoice-payment";
  import FormField from "../../components/Form/FormField.svelte";
  import DateInput from "../../components/Form/Fields/DateInput.svelte";
  import CompanyDropdown from "../../components/Business/CompanyDropdown.svelte";
  import OwnershipDropdown from "../../components/Form/Fields/Dropdown/OwnershipDropdown.svelte";
  import Switch from "../../components/Form/Fields/Switch.svelte";
  import Input from "../../components/Form/Fields/Input.svelte";
  import Button from "../../components/Button.svelte";
  import Icon from "../../components/Icon.svelte";
  import UnpaidStatesMultiSelect from "../../components/Business/UnpaidStatesMultiSelect.svelte";
  import UnpaidModal from "./UnpaidModal.svelte";
  import type { ICompanyInvoicePaymentDTO } from "../../services/DTO/entity/company-invoice-payment-dto";
  import type { ICompanyInvoicePaymentEmailPreviewDTO } from "../../services/DTO/entity/company-invoice-payment-email-preview-dto";
  import FormLayout from "../../layout/FormLayout.svelte";
  import NumberInput from "../../components/Form/Fields/NumberInput.svelte";
  import ClfDropdown from "../../components/Form/Fields/Dropdown/ClfDropdown.svelte";
  import Textarea from "../../components/Form/Fields/Textarea.svelte";
  import { showToast } from "../../utils/misc/toast-utils";
  import { load } from "../User/user.store";
  import CompanyInvoicePaymentReferentiDropdown from "../../components/Business/CompanyInvoicePaymentReferentiDropdown.svelte";
  import { CompanyInvoiceService } from "../../services/companyinvoice.service";

  let cansend: boolean = PermissionService.isAllowedTo(
    $user,
    "companyinvoices.cansend",
  );

  const editing = writable<boolean>(false);
  const deleting = writable<boolean>(false);
  const sendModalOpen = writable<boolean>(false);
  const selected = writable<ICompanyInvoicePaymentDTO[]>([]);
  let targets = [];

  async function onSubmitExisting() {
    await save();
    onCancel();
  }

  function updateSelected(items: ICompanyInvoicePaymentDTO[]) {
    targets = [];
    items.forEach((element) => {
      if (element.selected) targets.push(element);
    });
  }

  function onCancel() {
    editing.set(false);
    record.set({} as ICompanyInvoicePaymentDTO);
  }

  function onSendSingle(item: ICompanyInvoicePaymentDTO) {
    targets = [item];
    selected.set(targets);
    sendModalOpen.set(true);
  }

  function onSendSelected() {
    selected.set(targets);
    sendModalOpen.set(true);
  }

  async function onLoadWithUpdate() {
    await loadWithUpdate();
    showToast("Operazione completata", "is-success");
  }

  async function onSendEmails(
    pec: boolean,
    results: ICompanyInvoicePaymentEmailPreviewDTO[],
  ) {
    await sendEmails(pec, results, null);
    editing.set(false);
    sendModalOpen.set(false);
    record.set({} as ICompanyInvoicePaymentDTO);
    showToast("Operazione completata", "is-success");
  }

  function onCancelEmail() {
    sendModalOpen.set(false);
  }

  function onEdit(item: ICompanyInvoicePaymentDTO) {
    record.set(Object.assign({}, item));
    editing.set(true);
  }

  function onDeleteConfirmed(item: ICompanyInvoicePaymentDTO) {

    CompanyInvoiceService.deleteCompanyInvoicePayment(
      item.payment.oid,
    ).subscribe({
      next: () => {
        showToast("Rata eliminata correttamente", "is-success");
        initList(); // refresh table
      },
      error: (err) => {
        console.error("[Delete] Error:", err);
        showToast("Errore durante l'eliminazione", "is-danger");
      },
      complete: () => {
        deleting.set(false);
        record.set({} as ICompanyInvoicePaymentDTO);
      },
    });
  }

  onMount(async () => {
    await initList();
  });
</script>

{#if $editing === false}
  <UnpaidModal
    open={$sendModalOpen}
    targets={$selected}
    on:confirm={(e) => onSendEmails(e.detail.pec, e.detail.results)}
    on:decline={(e) => onCancelEmail()}
  />

  <SearchHeader
    entitySingular="insoluto"
    entityPlural="insoluti"
    numRecords={$records == null ? 0 : $records.length}
    bind:searchValue={$filter.criteria.query}
    on:search={() => search()}
    on:reset={() => resetSearchFilter()}
    clearButtonVisible={false}
    searchFieldVisible={false}
  >
    <div slot="search">
      <div class="level">
        <div class="level-item">
          <div class="level-item">
            <FormField>
              <DateInput
                name=""
                placeholder="Data Inizio"
                required={false}
                allowInput={true}
                bind:value={$filter.criteria.dataScadenzaFrom}
                on:dateChanged={search}
              />
            </FormField>
          </div>
          <div class="level-item">
            <FormField>
              <DateInput
                name=""
                placeholder="Data Fine"
                required={false}
                allowInput={true}
                bind:value={$filter.criteria.dataScadenzaTo}
                on:dateChanged={search}
              />
            </FormField>
          </div>
          <div class="level-item">
            <FormField>
              <CompanyDropdown
                name=""
                placeholder="Filtra per cliente..."
                required={false}
                clearVisible={true}
                bind:value={$filter.criteria.companyId}
                on:select={search}
              />
            </FormField>
          </div>
          <div class="level-item">
            <FormField>
              <OwnershipDropdown
                name=""
                placeholder="Filtra per az. proprietaria..."
                required={false}
                clearVisible={true}
                bind:value={$filter.criteria.ownershipid}
                on:select={search}
              />
            </FormField>
          </div>
          <div class="level-item">
            <FormField>
              <CompanyInvoicePaymentReferentiDropdown
                name=""
                placeholder="Filtra per referente..."
                required={false}
                clearVisible={true}
                bind:valuelabel={$filter.criteria.referente}
                on:select={search}
              />
            </FormField>
          </div>
        </div>
      </div>
      <div class="level">
        <div class="level-item">
          <div class="level-item">
            <FormField usageHelp="Stati">
              <UnpaidStatesMultiSelect bind:value={$filter.criteria.stati} />
            </FormField>
          </div>
        </div>

        <div class="level-item">
          <p class="control">
            <Button on:click={search}>
              <Icon name="search" />

              <span> Cerca </span>
            </Button>
          </p>
        </div>

        <div class="level-item">
          <p class="control">
            <Button on:click={resetSearchFilter}>Azzera</Button>
          </p>
        </div>

        {#if cansend === true}
          <div class="level-item">
            <p class="control">
              <Button on:click={(e) => onSendSelected()}
                >Invia a selezionati</Button
              >
            </p>
          </div>
        {/if}

        <div class="level-item">
          <p class="control">
            <Button on:click={(e) => onLoadWithUpdate()}
              >Aggiorna e ricarica</Button
            >
          </p>
        </div>
      </div>
    </div>
  </SearchHeader>

  <SelectableTable
    loading={$loading}
    fields={companyInvoicePaymentDtoFields}
    records={$records}
    pagination={null}
    createEnabled={false}
    showEnabled={cansend}
    deleteEnabled={true}
    downloadEnabled={false}
    customEnabled={cansend}
    customIcon="send"
    exportEnabled={true}
    on:export={(_) => onExport()}
    on:allupdate={(e) => updateSelected(e.detail)}
    on:custom={(e) => onSendSingle(e.detail)}
    on:edit={(e) => onEdit(e.detail)}
    on:delete={(e) => onDeleteConfirmed(e.detail)}
  />
{:else if $record}
  <div class="columns">
    <div class="column is-9">
      <FormLayout
        submitEnabled={cansend}
        canSubmit={cansend}
        on:submit={onSubmitExisting}
        on:cancel={onCancel}
      >
        <div class="columns">
          <div class="column is-4">
            <FormField>
              <OwnershipDropdown
                name="Azienda proprietaria"
                required={true}
                disabled={true}
                bind:value={$record.ownership.oid}
              />
            </FormField>
          </div>
          <div class="column is-4">
            <FormField>
              <Input
                name="Numero fattura"
                required={true}
                disabled={true}
                bind:value={$record.invoice.numeroFattura}
              />
            </FormField>
          </div>
          <div class="column is-4">
            <FormField>
              <NumberInput
                name="Importo rata"
                required={true}
                disabled={true}
                bind:value={$record.payment.importorata}
              />
            </FormField>
          </div>
        </div>
        <div class="columns">
          <div class="column is-3">
            <FormField>
              <DateInput
                name="Data emissione"
                required={true}
                disabled={true}
                bind:value={$record.invoice.dataEmissione}
              />
            </FormField>
          </div>

          <div class="column is-3">
            <FormField>
              <DateInput
                name="Data scadenza"
                required={true}
                disabled={true}
                bind:value={$record.payment.dataScadenza}
              />
            </FormField>
          </div>
        </div>

        <div class="columns">
          <div class="column is-12">
            <FormField>
              <Input
                name="Referente"
                required={false}
                disabled={true}
                bind:value={$record.payment.referente}
              />
            </FormField>
          </div>
        </div>

        <div class="columns">
          <div class="column is-4">
            <FormField>
              <ClfDropdown
                name="Stato"
                required={true}
                schema="STATSOLLEC"
                bind:value={$record.statosollecitoOid}
              />
            </FormField>
          </div>

          <div class="column is-8">
            <FormField>
              <Textarea
                name="Nota"
                placeholder="Nota"
                required={false}
                rows={3}
                bind:value={$record.payment.note}
              />
            </FormField>
          </div>
        </div>
      </FormLayout>
    </div>
    <div class="column is-3"></div>
  </div>
{/if}

<style>
  /*.add-contact-type {
    margin: auto;
    margin-bottom: 2rem;
  }
  .add-contact-type .control .radio {
    margin-left: 2rem;
    margin-right: 2rem;
  }*/
</style>
